import * as React from 'react';
import { FC } from 'react';
import { Container, NavLink } from 'reactstrap';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import { TK } from '../../store/translations/translationKeys';
import { getLanguage, useTranslations } from '../../store/translations/hooks';
import AppNameIcon from '../../shared/icons/AppNameIcon';
import AppIcon from '../../shared/icons/AppIcon';
import { Flag } from '../../components/Flag';
import { paths } from '../../app/constants';
import { saveAuthContext } from '../../shared/authContext';

const Title = styled.h2`
    text-align: center;
    margin-bottom: var(--spacer-M);
    font-size: 2rem;
    font-weight: 600;
    color: var(--text-body);
`;

const ContextCardsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: calc(2*var(--spacer-XS));
    margin-bottom: var(--spacer-M);
`;

const ContextCard = styled.button<{ children?: React.ReactNode; onClick?: () => void }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: var(--spacer-S);
    background: white;
    border: var(--border-medium) solid var(--appLigthGrey);
    border-radius: var(--spacer-XS);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: var(--appBlue);
        background-color: var(--bg-hover-light);
        transform: translateY(-2px);
        box-shadow: 0 var(--spacer-XXS) var(--spacer-S) rgba(0, 0, 0, 0.1);
    }

    &:active {
        transform: translateY(0);
    }
`;

const CardIcon = styled.div`
    font-size: 2rem;
    margin-bottom: var(--spacer-XS);
`;

const CardTitle = styled.h3`
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text-body);
    margin-bottom: var(--spacer-XXS);
`;

const CardDescription = styled.p`
    font-size: 0.9rem;
    color: var(--text-muted);
    text-align: center;
    margin: 0;
`;

const LanguageFlagWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: var(--spacer-XS);
    margin-top: var(--spacer-S);
`;

const CloseButton = styled.button`
    position: absolute;
    top: var(--spacer-XS);
    right: var(--spacer-XS);
    background: none;
    border: none;
    font-size: 1.5rem;
    color: var(--text-muted);
    cursor: pointer;
    padding: 0;
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s;
    z-index: 10;

    &:hover {
        color: var(--text-body);
    }
`;

const LoginContextChooser: FC = () => {
    const t = useTranslations();
    const history = useHistory();
    const location = useLocation();
    const supportedLanguages = ['pt', 'en', 'fr'] as const;
    const currentLanguage = getLanguage();

    const handleContextChoice = (context: 'professional' | 'client') => {
        if (context === 'client') {
            const companyId = location.hash.split('#')[1];
            const url = companyId
                ? `https://client.appointmint.pt/?companyId=${companyId}`
                : 'https://client.appointmint.pt/';
            window.location.href = url;
            return;
        }
        saveAuthContext(context);
        history.push(paths.loginProfessional() + location.hash);
    };

    return (
        <div className="loginPage">
            <div className="centerContainer">
                <a href="https://appointmint.pt/"><AppNameIcon className="appNameIcon"/></a>
                <AppIcon className="appIcon appIcon1" />
                <AppIcon className="appIcon appIcon2" />

                <div className="loginForm" style={{position: 'relative'}}>
                    <Title>{t(TK.signIn)}</Title>

                    <ContextCardsContainer>
                        <ContextCard id="login-chooser-professional" onClick={() => handleContextChoice('professional')}>
                            <CardTitle>{t(TK.professional)}</CardTitle>
                            <CardDescription>{t(TK.signInAsProfessional)}</CardDescription>
                        </ContextCard>

                        <ContextCard id="login-chooser-client" onClick={() => handleContextChoice('client')}>
                            <CardTitle>{t(TK.client)}</CardTitle>
                            <CardDescription>{t(TK.signInAsClient)}</CardDescription>
                        </ContextCard>
                    </ContextCardsContainer>

                    <LanguageFlagWrapper>
                        {supportedLanguages
                            .filter(lang => lang !== currentLanguage)
                            .map(lang => (
                                <NavLink key={lang} href={`/${lang}`}>
                                    <Flag flag={lang} />
                                </NavLink>
                            ))}
                    </LanguageFlagWrapper>
                </div>
            </div>
        </div>
    );
};

export default LoginContextChooser;
